import * as React from 'react';
import {
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
} from 'react';

import { ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import { withFormik } from 'formik';
import compose from 'lodash/fp/compose';
import keys from 'lodash/keys';

import StyledFormWrapper from '../../../../shared/components/styled-form-wrapper/styled-form-wrapper';
import { withHigherOrderComponent } from '../../../../shared/components/with-higher-order-component/with-higher-order-component';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { ChangeDetectorContext } from '../../../shared/components/change-detector-context/ChangeDetectorContext';
import { ChangeDetectorContextModel } from '../../../shared/components/change-detector-context/models/change-detector-context.model';
import { useCheckPath } from '../../../shared/hooks/use-check-path/use-check-path.hook';
import { formTheme } from '../../../shared/themes/form-theme/form.theme';
import { usePopulateCampaignDetailsForm } from '../../hooks/use-populate-campaign-details-form/use-populate-campaign-details-form.hook';
import { setVehiclePopulation } from '../../store/slices/campaigns-vehicle-population.slice';
import { CampaignVinFiltersContainer } from './components/campaign-vin-filters-container/campaign-vin-filters-container';
import withCreateCampaignFormData from './components/hoc/with-create-campaign-form-data/with-create-campaign-form-data';
import { useCreateNewCampaignOnSubmit } from './hooks/use-create-new-campaign-submit/use-create-new-campaign-submit.hook';
import { OuterVehiclePopulationFormPropsModel } from './models/outer-vehicle-population-form-props.model';
import { VehiclePopulationFormikModel } from './models/vehicle-population-formik.model';
import { VehiclePopulationComponentProps } from './types/vehicle-population-component.props';
import { vehiclePopulationResponseModelToFormik } from './utils/vehicle-population-response-model-to-formik/vehicle-population-response-model-to-formik.util';
import { VehiclePopulationActionButtons } from './vehicle-population-action-buttons/vehicle-population-action-buttons';

const VehiclePopulationFormContainer: FunctionComponent<
  VehiclePopulationComponentProps
> = (
  props: PropsWithChildren<VehiclePopulationComponentProps>
): ReactElement => {
  const { children, handleSubmit, values, setValues, touched } = props;

  const cdContext = useContext(
    ChangeDetectorContext
  ) as ChangeDetectorContextModel;

  const dispatch = useAppDispatch();

  const [isOnEdit, { id: campaignId }] = useCheckPath(
    '/campaign-management/edit/'
  );
  const data = useAppSelector(
    state => state.campaignsFeature.vehiclePopulation.data
  );

  useEffect(() => {
    dispatch(setVehiclePopulation(values));
  }, [values]);

  useEffect(() => {
    cdContext.setIsChanged(keys(touched).length > 0);
  }, [touched]);

  usePopulateCampaignDetailsForm<VehiclePopulationFormikModel>(
    isOnEdit,
    campaignId,
    setValues,
    vehiclePopulationResponseModelToFormik,
    data
  );

  return (
    <ThemeProvider theme={formTheme}>
      <Box
        sx={{
          my: '48px',
        }}
        data-testid="campaign-vehicle-population"
      >
        <StyledFormWrapper onSubmit={handleSubmit}>
          {children}
        </StyledFormWrapper>
      </Box>
    </ThemeProvider>
  );
};

const vehiclePopulationFormik = withFormik<
  OuterVehiclePopulationFormPropsModel,
  VehiclePopulationFormikModel
>({
  mapPropsToValues: (props: OuterVehiclePopulationFormPropsModel) => ({
    vinCriteria: props.vehiclePopulationStateFields?.vinCriteria ?? [''],
  }),
  handleSubmit: useCreateNewCampaignOnSubmit(),
})(
  compose(
    withHigherOrderComponent(CampaignVinFiltersContainer),
    withHigherOrderComponent(VehiclePopulationActionButtons)
  )(VehiclePopulationFormContainer)
);

export default withCreateCampaignFormData(vehiclePopulationFormik);
